import React, { useEffect, useState } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import { api } from "../lib/api";
import {
  LayoutDashboard, Briefcase, Sparkles, CreditCard, Settings2, Shield, LogOut,
} from "lucide-react";

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const nav = useNavigate();
  const location = useLocation();
  const [adminAlerts, setAdminAlerts] = useState(null); // {has_notifications, total, alerts:[...]}

  // Notifications polling — only active when admin is on an /admin* route.
  // On other routes we keep the last known value (no interval) so the sidebar dot
  // still reflects fresh-but-stale state without spamming the API on every authenticated page.
  const onAdminRoute = location.pathname.startsWith("/admin");
  useEffect(() => {
    if (user?.role !== "admin") return;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/admin/notifications");
        if (!cancelled) setAdminAlerts(r.data);
      } catch (_) { /* noop */ }
    };
    load();  // one-shot fetch on mount / route change to keep dot fresh
    if (!onAdminRoute) return () => { cancelled = true; };
    // Only set up the 60s polling interval when on admin routes
    const id = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user?.role, onAdminRoute]);

  const items = [
    { to: "/dashboard", label: t("nav.dashboard"), Icon: LayoutDashboard, testid: "side-dashboard" },
    { to: "/matters", label: t("dash.matters"), Icon: Briefcase, testid: "side-matters" },
    { to: "/new-analysis", label: t("dash.newAnalysis"), Icon: Sparkles, testid: "side-new-analysis" },
    { to: "/pricing", label: t("nav.pricing"), Icon: CreditCard, testid: "side-pricing" },
    { to: "/settings", label: lang === "el" ? "Ρυθμίσεις" : "Settings", Icon: Settings2, testid: "side-settings" },
  ];
  if (user?.role === "admin") {
    items.push({
      to: "/admin", label: "Admin", Icon: Shield, testid: "side-admin",
      notifCount: adminAlerts?.has_notifications ? (adminAlerts.total || 0) : 0,
      notifSeverity: (adminAlerts?.alerts || []).some((a) => a.severity === "error") ? "error" : "warn",
    });
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex">
      <aside className="hidden md:flex md:flex-col w-64 bg-white border-r border-slate-200 p-5">
        <Link to="/" className="flex items-center gap-2 mb-10" data-testid="sidebar-brand">
          <img src="/dikigoros-logo.png" alt="Dikigoros.io" className="h-8 w-auto" width={800} height={239} />
        </Link>
        <nav className="space-y-1">
          {items.map(({ to, label, Icon, testid, notifCount, notifSeverity }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/dashboard"}
              data-testid={testid}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition relative ${
                  isActive
                    ? "bg-[#0A192F] text-white shadow-[0_8px_18px_rgba(11,59,111,0.18)]"
                    : "text-slate-600 hover:bg-slate-50"
                }`
              }
              title={notifCount ? `${notifCount} alert${notifCount > 1 ? "s" : ""}` : undefined}
            >
              <span className="relative">
                <Icon className="h-4 w-4" />
                {notifCount > 0 && (
                  <span className={`absolute -top-1 -right-1 h-2 w-2 rounded-full ring-2 ring-white ${
                    notifSeverity === "error" ? "bg-rose-500" : "bg-amber-500"
                  } animate-pulse`} data-testid="admin-notif-dot" />
                )}
              </span>
              <span className="font-medium">{label}</span>
              {notifCount > 0 && (
                <span className={`ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                  notifSeverity === "error" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-800"
                }`} data-testid="admin-notif-count">{notifCount}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto pt-6 border-t border-slate-200">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-8 w-8 rounded-full bg-[#0A192F] text-white flex items-center justify-center text-sm font-semibold">
              {(user?.full_name || user?.email || "?").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">{user?.full_name || user?.email}</div>
              <div className="text-xs text-slate-500 truncate">{user?.plan || "Free"}</div>
            </div>
          </div>
          <button
            onClick={async () => { await logout(); nav("/"); }}
            className="w-full flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 px-3 py-2 rounded-lg hover:bg-slate-50"
            data-testid="sidebar-logout"
          >
            <LogOut className="h-4 w-4" /> {t("nav.logout")}
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6">
          <div className="text-sm text-slate-500">Dikigoros<span className="text-[#8B1E3F]">.io</span></div>
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center rounded-full border border-slate-200 bg-white p-0.5" data-testid="app-lang-toggle">
              {["el", "en"].map((code) => (
                <button
                  key={code}
                  onClick={() => setLang(code)}
                  data-testid={`app-lang-${code}`}
                  className={`px-3 py-1 text-xs font-semibold tracking-wide rounded-full transition ${
                    lang === code ? "bg-[#0A192F] text-white" : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  {code.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </header>
        <main className="flex-1 p-6 md:p-10">{children}</main>
      </div>
    </div>
  );
}
